import React from 'react';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { FaCrown, FaLock, FaArrowUp } from 'react-icons/fa';
import BotonUpgradeEpayco from '../ePayco/BotonUpgradeEpayco';

const FeatureGuard = ({ 
  feature, 
  children, 
  fallback = null,
  showUpgradePrompt = true,
  className = ''
}) => {
  const { canAccess, isPremium, isFree } = useFeatureAccess();

  // Si tiene acceso, mostrar el contenido
  if (canAccess(feature)) {
    return <>{children}</>;
  }

  // Si no tiene acceso y hay un fallback, mostrarlo
  if (fallback) {
    return <>{fallback}</>;
  }

  // Si no tiene acceso y no hay fallback, mostrar prompt de upgrade
  if (showUpgradePrompt && isFree) {
    return (
      <>
        <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 text-center ${className}`}>
          <div className="mb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <FaLock className="text-blue-600 text-2xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Característica Premium
            </h3>
            <p className="text-gray-600 mb-4">
              Esta característica está disponible exclusivamente para usuarios premium.
              Actualiza tu plan para desbloquear acceso completo.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <BotonUpgradeEpayco
              className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              onSuccess={(epaycoData) => {
                console.log('Upgrade iniciado desde FeatureGuard:', epaycoData);
              }}
              onError={(error) => {
                console.error('Error en upgrade desde FeatureGuard:', error);
              }}
            >
              <FaCrown className="mr-2" />
              Actualizar a Premium
            </BotonUpgradeEpayco>
            
            <BotonUpgradeEpayco
              className="inline-flex items-center justify-center px-6 py-3 border border-blue-600 text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition-colors"
              onSuccess={(epaycoData) => {
                console.log('Upgrade iniciado desde FeatureGuard (Ver Planes):', epaycoData);
              }}
              onError={(error) => {
                console.error('Error en upgrade desde FeatureGuard (Ver Planes):', error);
              }}
            >
              <FaArrowUp className="mr-2" />
              Ver Planes
            </BotonUpgradeEpayco>
          </div>

          <div className="mt-4 text-sm text-gray-500">
            <p>¿Ya tienes una cuenta premium? <a href="#" className="text-blue-600 hover:underline">Inicia sesión</a></p>
          </div>
        </div>
      </>
    );
  }

  // Si no es usuario free, mostrar mensaje genérico
  return (
    <div className={`bg-gray-50 border border-gray-200 rounded-lg p-6 text-center ${className}`}>
      <div className="mb-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
          <FaLock className="text-gray-600 text-2xl" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Acceso Restringido
        </h3>
        <p className="text-gray-600">
          No tienes acceso a esta característica en tu plan actual.
        </p>
      </div>
    </div>
  );
};

// Componente para múltiples características
const MultiFeatureGuard = ({ 
  features, 
  children, 
  fallback = null,
  requireAll = false,
  showUpgradePrompt = true,
  className = ''
}) => {
  const { canAccessAll, canAccessAny, isFree } = useFeatureAccess();

  const hasAccess = requireAll ? canAccessAll(features) : canAccessAny(features);

  // Si tiene acceso, mostrar el contenido
  if (hasAccess) {
    return <>{children}</>;
  }

  // Si no tiene acceso y hay un fallback, mostrarlo
  if (fallback) {
    return <>{fallback}</>;
  }

  // Si no tiene acceso y no hay fallback, mostrar prompt de upgrade
  if (showUpgradePrompt && isFree) {
    return (
      <>
        <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6 text-center ${className}`}>
          <div className="mb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <FaLock className="text-blue-600 text-2xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Características Premium
            </h3>
            <p className="text-gray-600 mb-4">
              {requireAll 
                ? 'Todas estas características están disponibles exclusivamente para usuarios premium.'
                : 'Algunas de estas características están disponibles exclusivamente para usuarios premium.'
              }
              Actualiza tu plan para desbloquear acceso completo.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <BotonUpgradeEpayco
              className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              onSuccess={(epaycoData) => {
                console.log('Upgrade iniciado desde MultiFeatureGuard:', epaycoData);
              }}
              onError={(error) => {
                console.error('Error en upgrade desde MultiFeatureGuard:', error);
              }}
            >
              <FaCrown className="mr-2" />
              Actualizar a Premium
            </BotonUpgradeEpayco>
            
            <BotonUpgradeEpayco
              className="inline-flex items-center justify-center px-6 py-3 border border-blue-600 text-blue-600 font-medium rounded-lg hover:bg-blue-50 transition-colors"
              onSuccess={(epaycoData) => {
                console.log('Upgrade iniciado desde MultiFeatureGuard (Ver Planes):', epaycoData);
              }}
              onError={(error) => {
                console.error('Error en upgrade desde MultiFeatureGuard (Ver Planes):', error);
              }}
            >
              <FaArrowUp className="mr-2" />
              Ver Planes
            </BotonUpgradeEpayco>
          </div>
        </div>
      </>
    );
  }

  // Si no es usuario free, mostrar mensaje genérico
  return (
    <div className={`bg-gray-50 border border-gray-200 rounded-lg p-6 text-center ${className}`}>
      <div className="mb-4">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
          <FaLock className="text-gray-600 text-2xl" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Acceso Restringido
        </h3>
        <p className="text-gray-600">
          No tienes acceso a estas características en tu plan actual.
        </p>
      </div>
    </div>
  );
};

// Exportar ambos componentes
export { FeatureGuard, MultiFeatureGuard };
export default FeatureGuard; 